import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { Box, Grid } from '@mui/material';
import TablePagination from '@mui/material/TablePagination';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import ContributorRow from '../ContributorsRow';
import { tableStyles } from './styled';
import { Label, SearchInputWrapper } from 'src/shared';
import {
    TableControlsContainer,
    TableEditContainer
} from 'src/pages/Community/CommunityContributors/styled';
import { StyledToolTip } from 'src/pages/Community/CommunityContributors/components';
import { HistoryText, labelStyle } from '../../../ProjectDashboard/styled';
import SortIcon from 'src/utils/SortIcon';
import { IProjectContributor, IProjectData } from 'src/types/projects';
import color from 'src/theme/color';

export interface Props {
    projectData: IProjectData;
    handleSort: (sortBy: string) => void;
    sort: string;
    sortBy: string | null;
}

const ProjectContributors: FunctionComponent<Props> = ({
    projectData,
    handleSort,
    sort,
    sortBy
}: Props) => {
    const [search, setSearch] = useState<string>('');
    const [page, setPage] = useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = useState<number>(20);
    const pageStart = page * rowsPerPage;
    const pageEnd = (page + 1) * rowsPerPage - 1;
    const contributors = projectData?.contributors;

    const filteredContributors = useMemo(() => {
        if (search && contributors) {
            const lowercaseQuery = search.toLowerCase();
            const filteredRows = contributors.filter(
                (row: IProjectContributor) =>
                    row.name.toLowerCase().includes(lowercaseQuery)
            );
            return filteredRows;
        } else {
            return contributors || [];
        }
    }, [search, contributors]);

    useEffect(() => {
        setPage(0);
    }, [search, contributors]);

    const contributorsSlice = filteredContributors.slice(pageStart, pageEnd);
    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        const input = event.target.value;
        setSearch(input);
    };
    const handleChangePage = (
        _event: React.MouseEvent<HTMLButtonElement> | null,
        pageChanged: number
    ) => {
        setPage(pageChanged);
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };
    return (
        <Box sx={tableStyles.root} marginTop={2}>
            <TableControlsContainer sx={tableStyles.tableControls}>
                <Grid
                    container
                    spacing={2}
                    columns={{ xs: 12, sm: 12, md: 12, lg: 12 }}
                >
                    <Grid
                        item
                        xs={12}
                        sm={12}
                        md={7}
                        lg={7}
                        sx={{
                            color: color.white,
                            display: {
                                xs: 'block',
                                sm: 'block',
                                md: 'block',
                                lg: 'block'
                            }
                        }}
                    >
                        <Box sx={{ mb: 1, mt: 4 }}>
                            <Label sxOverrides={HistoryText}>
                                Contributors
                            </Label>
                        </Box>
                        <Box>
                            <Label sxOverrides={labelStyle}>
                                The following is the history of all the work
                                this contributor interacted with on this
                                project. Status reflects the contributor&apos;s
                                current availability (active, away, offline, or
                                archive).
                            </Label>
                        </Box>
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        sm={12}
                        md={5}
                        lg={5}
                        sx={{
                            display: 'flex',
                            alignItems: {
                                xs: 'flex-end',
                                sm: 'flex-end',
                                md: 'flex-end'
                            }
                        }}
                    >
                        <TableEditContainer>
                            <SearchInputWrapper
                                search={search}
                                handleSearch={handleSearch}
                                sx={{
                                    width: {
                                        xs: '300px',
                                        sm: '300px',
                                        md: '500px',
                                        lg: '500px'
                                    }
                                }}
                            />
                        </TableEditContainer>
                    </Grid>
                </Grid>
            </TableControlsContainer>
            <TableContainer component={Paper} sx={tableStyles.container}>
                <Table
                    stickyHeader
                    sx={tableStyles.table}
                    aria-label="simple table"
                >
                    <TableHead sx={tableStyles.headRow}>
                        <TableRow sx={tableStyles.dataRow}>
                            <TableCell
                                sx={{
                                    ...tableStyles.muiTableCell,
                                    ...tableStyles.firstHeadCell,
                                    ...tableStyles.headCell
                                }}
                            >
                                <Label
                                    component="span"
                                    sxOverrides={tableStyles.baseLabel}
                                >
                                    Name
                                </Label>
                            </TableCell>
                            <TableCell
                                sx={{
                                    ...tableStyles.muiTableCell,
                                    ...tableStyles.headCell,
                                    ...tableStyles.headCellXsPlus,
                                    ...tableStyles.pointer
                                }}
                                onClick={() => handleSort('status')}
                            >
                                <StyledToolTip
                                    title="Contributor availability status. Hover the cell to see the last update time."
                                    placement="top"
                                >
                                    <Label
                                        component="span"
                                        sxOverrides={tableStyles.baseLabel}
                                    >
                                        Availability
                                        {sortBy === 'status' && (
                                            <SortIcon sort={sort} />
                                        )}
                                    </Label>
                                </StyledToolTip>
                            </TableCell>
                            <TableCell
                                sx={{
                                    ...tableStyles.muiTableCell,
                                    ...tableStyles.headCell,
                                    ...tableStyles.headCellXl,
                                    ...tableStyles.pointer
                                }}
                                onClick={() => handleSort('lastEngagement')}
                            >
                                <Label
                                    component="span"
                                    sxOverrides={tableStyles.baseLabel}
                                >
                                    Last Engagement
                                    {sortBy === 'lastEngagement' && (
                                        <SortIcon sort={sort} />
                                    )}
                                </Label>
                            </TableCell>
                            <TableCell
                                sx={{
                                    ...tableStyles.muiTableCell,
                                    ...tableStyles.headCell,
                                    ...tableStyles.headCellMd,
                                    ...tableStyles.pointer
                                }}
                                onClick={() => handleSort('reservedWorks')}
                            >
                                <Label
                                    component="span"
                                    sxOverrides={tableStyles.baseLabel}
                                >
                                    Reserved works
                                    {sortBy === 'reservedWorks' && (
                                        <SortIcon sort={sort} />
                                    )}
                                </Label>
                            </TableCell>
                            <TableCell
                                sx={{
                                    ...tableStyles.muiTableCell,
                                    ...tableStyles.headCell,
                                    ...tableStyles.pointer
                                }}
                                onClick={() => handleSort('hourlyRate')}
                            >
                                <Label
                                    component="span"
                                    sxOverrides={tableStyles.baseLabel}
                                >
                                    Hourly rate
                                    {sortBy === 'hourlyRate' && (
                                        <SortIcon sort={sort} />
                                    )}
                                </Label>
                            </TableCell>
                            <TableCell
                                sx={{
                                    ...tableStyles.muiTableCell,
                                    ...tableStyles.headCell,
                                    ...tableStyles.lastHeadCell
                                }}
                            >
                                <Label
                                    component="span"
                                    sxOverrides={tableStyles.baseLabel}
                                >
                                    Skills
                                </Label>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody style={tableStyles.body}>
                        {contributorsSlice.map((row, i) => (
                            <ContributorRow row={row} key={i} />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                sx={{
                    ...tableStyles.pagination,
                    ...tableStyles.paginationLabel,
                    ' svg': tableStyles.caret,
                    '& .MuiTablePagination-select': tableStyles.paginationSelect
                }}
                component="div"
                count={filteredContributors.length}
                page={page}
                rowsPerPageOptions={[5, 20, 50, 100]}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </Box>
    );
};

export default ProjectContributors;
